<script setup>
import { ref, reactive, onMounted } from 'vue'
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	ArrowLeftOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import axios from 'axios'

// Реактивные данные
const mode = ref('list') // 'list' или 'form'
const isLoading = ref(false)
const isEditing = ref(false)
const items = ref([])

const formData = reactive({
	id: null,
	name: '',
	price: null,
	description: ''
})

const columns = [
	{
		title: 'ID',
		dataIndex: 'id',
		key: 'id',
		width: 80,
		sorter: (a, b) => a.id - b.id
	},
	{
		title: 'Название',
		dataIndex: 'name',
		key: 'name',
		ellipsis: true
	},
	{
		title: 'Цена',
		dataIndex: 'price',
		key: 'price',
		width: 120,
		sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price)
	},
	{
		title: 'Описание',
		dataIndex: 'description',
		key: 'description',
		ellipsis: true
	},
	{
		title: 'Действия',
		key: 'actions',
		width: 200,
		fixed: 'right'
	}
]

// Методы
async function loadData() {
	isLoading.value = true
	try {
		const response = await axios.get('/api/products?order[id]=desc')
		// Фильтруем данные, чтобы исключить пустые или некорректные записи
		items.value = response.data.filter(item => item && item.id)
		console.log('Загруженные товары:', items.value)
	} catch (error) {
		console.error('Ошибка загрузки товаров:', error)
		message.error('Ошибка загрузки товаров')
		items.value = []
	} finally {
		isLoading.value = false
	}
}

function createMode() {
	mode.value = 'form'
	isEditing.value = false
	Object.assign(formData, {
		id: null,
		name: '',
		price: null,
		description: ''
	})
}

function editItem(item) {
	mode.value = 'form'
	isEditing.value = true
	Object.assign(formData, {
		id: item.id,
		name: item.name,
		price: parseFloat(item.price),
		description: item.description || ''
	})
}

async function saveItem() {
	// Валидация
	if (!formData.name || !formData.price) {
		message.error('Заполните обязательные поля')
		return
	}
	
	isLoading.value = true
	try {
		const data = {
			name: formData.name.trim(),
			price: parseFloat(formData.price).toFixed(2),
			description: formData.description ? formData.description.trim() : ''
		}
		
		if (isEditing.value) {
			await axios.patch(`/api/products/${formData.id}`, data, {
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			})
			message.success('Товар успешно обновлен')
		} else {
			await axios.post('/api/products', data, {
				headers: {
					'Content-Type': 'application/json'
				}
			})
			message.success('Товар успешно создан')
		}
		
		mode.value = 'list'
		await loadData()
	} catch (error) {
		console.error('Ошибка сохранения:', error)
		if (error.response && error.response.data) {
			message.error('Ошибка сохранения: ' + (error.response.data.message || 'Неизвестная ошибка'))
		} else {
			message.error('Ошибка сохранения товара')
		}
	} finally {
		isLoading.value = false
	}
}

async function deleteItem(id) {
	isLoading.value = true
	try {
		await axios.delete(`/api/products/${id}`)
		message.success('Товар успешно удален')
		await loadData()
	} catch (error) {
		console.error('Ошибка удаления:', error)
		message.error('Ошибка удаления товара')
	} finally {
		isLoading.value = false
	}
}

// Lifecycle
onMounted(async () => {
	await loadData()
})
</script>

<template>
	<div class="products-admin">
		<!-- Список товаров -->
		<div v-if="mode === 'list'">
			<div class="products-header">
				<a-button
					type="primary"
					@click="createMode"
					style="background: linear-gradient(135deg, #F7C100 0%, #F69400 100%); border: none; margin-bottom: 20px;"
				>
					<template #icon>
						<PlusOutlined/>
					</template>
					Добавить товар
				</a-button>
			</div>
			
			<a-spin :spinning="isLoading">
				<div v-if="items.length > 0">
					<a-table
						:dataSource="items"
						:columns="columns"
						:pagination="false"
						row-key="id"
						:scroll="{ x: 800 }"
						size="middle"
					>
						<template #bodyCell="{ column, record }">
							<template v-if="column.key === 'price'">
								<span style="font-weight: 600; color: #52c41a;">{{ record.price }} ₽</span>
							</template>
							<template v-if="column.key === 'description'">
								<span style="color: #666;">{{ record.description || '—' }}</span>
							</template>
							<template v-if="column.key === 'actions'">
								<a-space>
									<a-button
										type="link"
										@click="editItem(record)"
										style="color: #F69400; padding: 4px 8px;"
										size="small"
									>
										<template #icon>
											<EditOutlined/>
										</template>
										Редактировать
									</a-button>
									<a-popconfirm
										title="Вы уверены, что хотите удалить этот товар?"
										@confirm="deleteItem(record.id)"
										ok-text="Да"
										cancel-text="Отмена"
									>
										<a-button
											type="link"
											danger
											size="small"
											style="padding: 4px 8px;"
										>
											<template #icon>
												<DeleteOutlined/>
											</template>
											Удалить
										</a-button>
									</a-popconfirm>
								</a-space>
							</template>
						</template>
					</a-table>
				</div>
			</a-spin>
			
			<div v-if="!isLoading && items.length === 0" class="empty-state">
				<a-empty description="Товары не найдены">
				</a-empty>
			</div>
		</div>
		
		<!-- Форма создания/редактирования -->
		<div v-if="mode === 'form'">
			<div class="form-header">
				<a-button
					type="text"
					@click="mode = 'list'"
					style="color: #F69400; margin-bottom: 20px;"
				>
					<template #icon>
						<ArrowLeftOutlined/>
					</template>
					К списку товаров
				</a-button>
				<h2 style="color: #333; margin-bottom: 30px;">
					{{ isEditing ? 'Редактировать товар' : 'Создать новый товар' }}
				</h2>
			</div>
			
			<div class="form-container">
				<a-form
					:model="formData"
					layout="vertical"
					@finish="saveItem"
				>
					<a-row :gutter="24">
						<a-col :span="24">
							<a-form-item
								label="Название товара"
								name="name"
								:rules="[{ required: true, message: 'Введите название товара' }]"
							>
								<a-input
									v-model:value="formData.name"
									placeholder="Например: Игровой фильм"
									size="large"
								/>
							</a-form-item>
						</a-col>
					</a-row>
					
					<a-row :gutter="24">
						<a-col :span="12">
							<a-form-item
								label="Цена (руб.)"
								name="price"
								:rules="[
                  { required: true, message: 'Введите цену товара' },
                  { type: 'number', min: 0, message: 'Цена должна быть больше 0' }
                ]"
							>
								<a-input-number
									v-model:value="formData.price"
									placeholder="0.00"
									size="large"
									:min="0"
									:precision="2"
									style="width: 100%;"
									:formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
									:parser="value => value.replace(/\$\s?|(,*)/g, '')"
								/>
							</a-form-item>
						</a-col>
					</a-row>
					
					<a-row :gutter="24">
						<a-col :span="24">
							<a-form-item
								label="Описание"
								name="description"
							>
								<a-textarea
									v-model:value="formData.description"
									placeholder="Описание товара для участников"
									:rows="4"
									size="large"
								/>
							</a-form-item>
						</a-col>
					</a-row>
					
					<a-form-item>
						<a-space>
							<a-button
								type="primary"
								html-type="submit"
								:loading="isLoading"
								style="background: linear-gradient(135deg, #F7C100 0%, #F69400 100%); border: none;"
								size="large"
							>
								{{ isLoading ? 'Сохранение...' : 'Сохранить' }}
							</a-button>
							<a-button @click="mode = 'list'" size="large">
								Отмена
							</a-button>
						</a-space>
					</a-form-item>
				</a-form>
			</div>
		</div>
	</div>
</template>

<style scoped>
.products-admin {
	min-height: 400px;
}

.products-header {
	margin-bottom: 20px;
}

.form-header {
	margin-bottom: 30px;
}

.form-container {
	background: #fafafa;
	border-radius: 8px;
	padding: 30px;
	border: 1px solid #e8e8e8;
}

.empty-state {
	text-align: center;
	padding: 60px 20px;
	background: #fafafa;
	border-radius: 8px;
	margin-top: 20px;
}

/* Стили таблицы */
:deep(.ant-table) {
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:deep(.ant-table-thead > tr > th) {
	background: linear-gradient(135deg, #F7C100 0%, #F69400 100%);
	color: #fff;
	font-weight: 600;
	text-align: center;
	border: none;
	padding: 16px 12px;
}

:deep(.ant-table-tbody > tr > td) {
	background: #fff;
	color: #333;
	border-bottom: 1px solid #f0f0f0;
	text-align: center;
	padding: 12px;
}

:deep(.ant-table-tbody > tr:hover > td) {
	background: #fff7e6 !important;
}

:deep(.ant-table-tbody > tr:nth-child(even) > td) {
	background: #fafafa;
}

:deep(.ant-table-tbody > tr:nth-child(even):hover > td) {
	background: #fff7e6 !important;
}

/* Стили формы */
:deep(.ant-form-item-label > label) {
	color: #333;
	font-weight: 500;
	font-size: 14px;
}

:deep(.ant-input),
:deep(.ant-input-number),
:deep(.ant-select-selector),
:deep(.ant-input-number-input),
:deep(.ant-input-affix-wrapper) {
	background: #fff;
	border: 1px solid #d9d9d9;
	color: #333;
}

:deep(.ant-input:focus),
:deep(.ant-input-number:focus),
:deep(.ant-input-affix-wrapper:focus),
:deep(.ant-input-affix-wrapper-focused),
:deep(.ant-select-focused .ant-select-selector) {
	border-color: #F69400;
	box-shadow: 0 0 0 2px rgba(246, 148, 0, 0.2);
}

:deep(.ant-input:hover),
:deep(.ant-input-number:hover),
:deep(.ant-input-affix-wrapper:hover) {
	border-color: #F69400;
}

:deep(.ant-input::placeholder),
:deep(.ant-input-number input::placeholder) {
	color: #bfbfbf;
}

/* Стили кнопок */
:deep(.ant-btn-link) {
	padding: 4px 8px;
	height: auto;
	font-size: 13px;
}

:deep(.ant-btn-link:hover) {
	text-decoration: none;
}

/* Empty состояние */
:deep(.ant-empty-description) {
	color: #999;
}

:deep(.ant-empty-image) {
	opacity: 0.6;
}

/* Спиннер */
:deep(.ant-spin-dot) {
	color: #F69400;
}

/* Popconfirm */
:deep(.ant-popover-inner-content) {
	padding: 12px 16px;
}
</style>